import { useState } from 'react';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { apiClient } from '@/api/client';

const APIDocsPage = () => {
  const [testText, setTestText] = useState('Hello, this is a test of ameboGPT.');
  const [testLanguage, setTestLanguage] = useState<'english' | 'hausa' | 'igbo' | 'yoruba'>('english');
  const [testVoice, setTestVoice] = useState('idera');
  const [testResult, setTestResult] = useState<any>(null);
  const [isTestLoading, setIsTestLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const { data: keys } = useQuery('api-keys', () => apiClient.getUserAPIKeys());
  const activeKey = keys?.find(k => k.is_active);

  const handleTestAPI = async () => {
    if (!activeKey) {
      toast.error('Please create an API key first');
      return;
    }

    setIsTestLoading(true);
    setTestResult(null);
    setAudioUrl(null);

    try {
      const response = await apiClient.generateTTS({
        text: testText,
        language: testLanguage,
        speaker_name: testVoice,
        temperature: 0.1,
        repetition_penalty: 1.1,
      });

      setTestResult({
        success: true,
        data: response,
      });

      // Build audio URL
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
      const fullAudioUrl = response.audio_url.startsWith('http')
        ? response.audio_url
        : `${baseUrl}${response.audio_url}`;
      setAudioUrl(fullAudioUrl);

      toast.success('API test successful!');
    } catch (error: any) {
      setTestResult({
        success: false,
        error: error.message || 'Request failed',
      });
      toast.error(error.message || 'API test failed');
    } finally {
      setIsTestLoading(false);
    }
  };

  const apiKey = activeKey?.key_prefix ? `${activeKey.key_prefix}••••••••` : 'YOUR_API_KEY';
  const baseUrl = import.meta.env.VITE_API_URL || 'https://api.amebogpt.com.ng';

  return (
    <div>
      <div className="title-one mb-40">
        <h2>API Documentation</h2>
        <p className="text-dark">Learn how to integrate ameboGPT into your applications</p>
      </div>

      {/* Quick Start */}
      <div className="card-style-one border-30 bg-white mb-30">
        <h3 className="title mb-4">🚀 Quick Start</h3>
        <p className="text-dark mb-4">
          Make your first API request in minutes. All you need is your API key.
        </p>

        <div className="bg-dark text-light p-4 rounded mb-4" style={{ fontFamily: 'monospace' }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-success">POST /tts</span>
            <button 
              className="btn btn-sm btn-outline-light"
              onClick={() => {
                navigator.clipboard.writeText(`curl -X POST "${baseUrl}/tts" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{"text": "Hello, I am ameboGPT!", "language": "english", "speaker_name": "idera"}'`);
                toast.success('Copied to clipboard!');
              }}
            >
              Copy
            </button>
          </div>
          <pre className="mb-0 text-light" style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>
{`curl -X POST "${baseUrl}/tts" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{"text": "Hello, I am ameboGPT!", "language": "english", "speaker_name": "idera"}'`}
          </pre>
        </div>
      </div>

      {/* API Sandbox */}
      <div className="card-style-one border-30 bg-white mb-30">
        <h3 className="title mb-4">🧪 API Sandbox</h3>
        <p className="text-dark mb-4">
          Test your API key right here. This uses your actual API quota.
        </p>

        {!activeKey ? (
          <div className="alert alert-warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            You need to create an API key first to use the sandbox.
          </div>
        ) : (
          <>
            <div className="row mb-4">
              <div className="col-md-12 mb-3">
                <label className="form-label fw-bold">Text to convert</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={testText}
                  onChange={(e) => setTestText(e.target.value)}
                  placeholder="Enter text to convert to speech..."
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Language</label>
                <select 
                  className="form-control"
                  value={testLanguage}
                  onChange={(e) => setTestLanguage(e.target.value as 'english' | 'hausa' | 'igbo' | 'yoruba')}
                >
                  <option value="english">English (Nigerian Accent)</option>
                  <option value="hausa">Hausa</option>
                  <option value="igbo">Igbo</option>
                  <option value="yoruba">Yoruba</option>
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold">Voice</label>
                <select 
                  className="form-control"
                  value={testVoice}
                  onChange={(e) => setTestVoice(e.target.value)}
                >
                  <option value="idera">Idera (Female)</option>
                  <option value="chinenye">Chinenye (Female)</option>
                  <option value="joke">Joke (Female)</option>
                  <option value="osagie">Osagie (Male)</option>
                  <option value="tunde">Tunde (Male)</option>
                </select>
              </div>
            </div>

            <button
              className="btn-one mb-4"
              onClick={handleTestAPI}
              disabled={isTestLoading || !testText.trim()}
            >
              {isTestLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Testing...
                </>
              ) : (
                <>
                  <i className="bi bi-play-fill me-2"></i>
                  Test API
                </>
              )}
            </button>

            {/* Test Result */}
            {testResult && (
              <div className={`alert ${testResult.success ? 'alert-success' : 'alert-danger'}`}>
                <h5 className="mb-3">
                  {testResult.success ? '✅ Success!' : '❌ Error'}
                </h5>
                <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>
                  {JSON.stringify(testResult.success ? testResult.data : { error: testResult.error }, null, 2)}
                </pre>
              </div>
            )}

            {/* Audio Player */}
            {audioUrl && (
              <div className="mt-4 p-4 bg-light rounded">
                <h5 className="mb-3">🔊 Generated Audio</h5>
                <audio controls className="w-100" src={audioUrl}>
                  Your browser does not support the audio element.
                </audio>
              </div>
            )}
          </>
        )}
      </div>

      {/* Endpoints */}
      <div className="card-style-one border-30 bg-white mb-30">
        <h3 className="title mb-4">📡 API Endpoints</h3>
        
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Method</th>
                <th>Endpoint</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><span className="badge bg-success">POST</span></td>
                <td><code>/tts</code></td>
                <td>Convert text to speech</td>
              </tr>
              <tr>
                <td><span className="badge bg-primary">GET</span></td>
                <td><code>/voices</code></td>
                <td>Get available voices (legacy)</td>
              </tr>
              <tr>
                <td><span className="badge bg-primary">GET</span></td>
                <td><code>/voices/all</code></td>
                <td>Get all voices by language</td>
              </tr>
              <tr>
                <td><span className="badge bg-primary">GET</span></td>
                <td><code>/health</code></td>
                <td>Check API health status</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Request Parameters */}
      <div className="card-style-one border-30 bg-white mb-30">
        <h3 className="title mb-4">📝 TTS Request Parameters</h3>
        
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Type</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>text</code></td>
                <td>string</td>
                <td>Yes</td>
                <td>The text to convert to speech (max 5000 characters)</td>
              </tr>
              <tr>
                <td><code>language</code></td>
                <td>string</td>
                <td>No</td>
                <td>Language: "english", "hausa", "igbo", "yoruba" (default: "english")</td>
              </tr>
              <tr>
                <td><code>speaker_name</code></td>
                <td>string</td>
                <td>No</td>
                <td>Voice ID to use (default: "idera")</td>
              </tr>
              <tr>
                <td><code>temperature</code></td>
                <td>number</td>
                <td>No</td>
                <td>Voice variation (0.1-1.0, default: 0.1)</td>
              </tr>
              <tr>
                <td><code>repetition_penalty</code></td>
                <td>number</td>
                <td>No</td>
                <td>Reduce repetition (1.0-2.0, default: 1.1)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Code Examples */}
      <div className="card-style-one border-30 bg-white mb-30">
        <h3 className="title mb-4">💻 Code Examples</h3>

        {/* Python */}
        <div className="mb-4">
          <h5 className="mb-3">Python</h5>
          <div className="bg-dark text-light p-4 rounded" style={{ fontFamily: 'monospace' }}>
            <div className="d-flex justify-content-end mb-2">
              <button 
                className="btn btn-sm btn-outline-light"
                onClick={() => {
                  navigator.clipboard.writeText(`import requests

API_KEY = "${apiKey}"
BASE_URL = "${baseUrl}"

response = requests.post(
    f"{BASE_URL}/tts",
    headers={
        "Content-Type": "application/json",
        "X-API-Key": API_KEY
    },
    json={
        "text": "Hello, I am ameboGPT!",
        "language": "english",
        "speaker_name": "idera"
    }
)

data = response.json()
print(f"Audio URL: {BASE_URL}{data['audio_url']}")`);
                  toast.success('Copied!');
                }}
              >
                Copy
              </button>
            </div>
            <pre className="mb-0 text-light" style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>
{`import requests

API_KEY = "${apiKey}"
BASE_URL = "${baseUrl}"

response = requests.post(
    f"{BASE_URL}/tts",
    headers={
        "Content-Type": "application/json",
        "X-API-Key": API_KEY
    },
    json={
        "text": "Hello, I am ameboGPT!",
        "language": "english",
        "speaker_name": "idera"
    }
)

data = response.json()
print(f"Audio URL: {BASE_URL}{data['audio_url']}")`}
            </pre>
          </div>
        </div>

        {/* JavaScript */}
        <div className="mb-4">
          <h5 className="mb-3">JavaScript / Node.js</h5>
          <div className="bg-dark text-light p-4 rounded" style={{ fontFamily: 'monospace' }}>
            <div className="d-flex justify-content-end mb-2">
              <button 
                className="btn btn-sm btn-outline-light"
                onClick={() => {
                  navigator.clipboard.writeText(`const API_KEY = "${apiKey}";
const BASE_URL = "${baseUrl}";

const response = await fetch(\`\${BASE_URL}/tts\`, {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-API-Key": API_KEY
  },
  body: JSON.stringify({
    text: "Hello, I am ameboGPT!",
    language: "english",
    speaker_name: "idera"
  })
});

const data = await response.json();
console.log(\`Audio URL: \${BASE_URL}\${data.audio_url}\`);`);
                  toast.success('Copied!');
                }}
              >
                Copy
              </button>
            </div>
            <pre className="mb-0 text-light" style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>
{`const API_KEY = "${apiKey}";
const BASE_URL = "${baseUrl}";

const response = await fetch(\`\${BASE_URL}/tts\`, {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-API-Key": API_KEY
  },
  body: JSON.stringify({
    text: "Hello, I am ameboGPT!",
    language: "english",
    speaker_name: "idera"
  })
});

const data = await response.json();
console.log(\`Audio URL: \${BASE_URL}\${data.audio_url}\`);`}
            </pre>
          </div>
        </div>
      </div>

      {/* Response Format */}
      <div className="card-style-one border-30 bg-white mb-30">
        <h3 className="title mb-4">📤 Response Format</h3>
        
        <div className="bg-dark text-light p-4 rounded" style={{ fontFamily: 'monospace' }}>
          <pre className="mb-0 text-light" style={{ fontSize: '14px' }}>
{`{
  "request_id": "abc123-def456-ghi789",
  "audio_url": "/audio/abc123-def456-ghi789.wav",
  "text": "Hello, I am ameboGPT!",
  "speaker": "idera"
}`}
          </pre>
        </div>
        
        <p className="text-dark mt-4">
          <strong>Note:</strong> The <code>audio_url</code> is relative. Prepend the API base URL to get the full URL:
          <br />
          <code>{baseUrl}/audio/abc123-def456-ghi789.wav</code>
        </p>
      </div>

      {/* Error Codes */}
      <div className="card-style-one border-30 bg-white">
        <h3 className="title mb-4">⚠️ Error Codes</h3>
        
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Meaning</th>
                <th>Solution</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>400</code></td>
                <td>Bad Request</td>
                <td>Check your request parameters</td>
              </tr>
              <tr>
                <td><code>401</code></td>
                <td>Unauthorized</td>
                <td>Invalid or missing API key</td>
              </tr>
              <tr>
                <td><code>403</code></td>
                <td>Quota Exceeded</td>
                <td>Upgrade your plan for more characters</td>
              </tr>
              <tr>
                <td><code>429</code></td>
                <td>Rate Limited</td>
                <td>Too many requests, slow down</td>
              </tr>
              <tr>
                <td><code>500</code></td>
                <td>Server Error</td>
                <td>Try again or contact support</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default APIDocsPage;

